import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getAlerts, getQueues } from '../services/api';
import { FiPhone, FiPhoneIncoming, FiPhoneMissed, FiPhoneOff, FiCheckCircle, FiClock, FiAlertTriangle, FiActivity, FiUsers } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, PieChart, Pie } from 'recharts';
import MultiSelectQueues from '../components/MultiSelectQueues';

const DATE_FILTERS = [
  { label: 'اليوم', key: 'today' },
  { label: 'أمس', key: 'yesterday' },
  { label: 'آخر 7 أيام', key: '7days' },
  { label: 'هذا الشهر', key: 'month' },
  { label: 'الكل', key: 'all' },
];

const getDateRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today':
      return { from: today.toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'yesterday': {
      const y = new Date(today.getTime() - 86400000);
      return { from: y.toISOString(), to: today.toISOString() };
    }
    case '7days':
      return { from: new Date(today.getTime() - 7 * 86400000).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    default:
      return { from: '', to: '' };
  }
};

// Pie series colours — DELIBERATELY UNCHANGED (Batch 7).
// The calm token set has only ~5 distinct hues plus grays, so a token-derived
// 8-slot categorical palette measured WORSE than this one: the amber/red pair
// scored a normal-vision ΔE of 6.1 (below the 15 floor — most people cannot
// tell those two slices apart), versus 17.1 for the values below. Recolouring
// here would trade legible data for visual tidiness, so the series keep their
// existing hues. Only the CARD around the chart is restyled.
const COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#00796b', '#c2185b', '#455a64'];

// Single-series / chrome colours. SVG presentation attributes cannot resolve
// CSS custom properties, so these mirror the central token VALUES literally:
//   CHART_ACCENT = --crm-accent · CHART_GRID = --crm-border
//   CHART_AXIS_INK = --crm-text-muted
// One series means no categorical separation to preserve — safe to align.
const CHART_ACCENT = '#5b54d6';
const CHART_GRID = '#eaecef';
const CHART_AXIS_INK = '#6b7280';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState({ count: 0, alerts: [] });
  const [activeFilter, setActiveFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [queue, setQueue] = useState([]);
  const [agentExtension, setAgentExtension] = useState('');
  const [queues, setQueues] = useState([]);
  const [agents, setAgents] = useState([]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (queue && queue.length > 0) params.queue = queue.join(",");
      if (agentExtension) params.agentExtension = agentExtension;

      const [statsRes, alertsRes] = await Promise.all([
        getDashboardStats(params),
        getAlerts()
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, queue, agentExtension]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  
  useEffect(() => {
    getQueues().then(r => setQueues(r.data || [])).catch(() => {});
    if (user?.role === 'admin') {
      const token = localStorage.getItem('token');
      fetch('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setAgents(Array.isArray(d) ? d.filter(u => u.extension) : []))
        .catch(() => {});
    }
  }, [user]);

  const handleDateFilter = (key) => {
    setActiveFilter(key);
    setCustomFrom(''); setCustomTo('');
    const range = getDateRange(key);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const applyCustomRange = (from, to) => {
    setCustomFrom(from); setCustomTo(to);
    setActiveFilter('');
    setDateFrom(from ? new Date(from).toISOString() : '');
    if (to) { const d = new Date(to); d.setHours(23,59,59,999); setDateTo(d.toISOString()); }
    else setDateTo('');
  };

  if (loading) return <div className="crm-calm"><div className="crm-empty">جاري التحميل...</div></div>;

  const queueData = stats?.callsByQueue?.map((q, i) => ({
    name: q.queueName || 'غير محدد',
    value: parseInt(q.total),
    answered: parseInt(q.answered) || 0,
    missed: parseInt(q.missed) || 0,
    fill: COLORS[i % COLORS.length]
  })) || [];

  const hourData = stats?.callsByHour?.map(h => ({
    hour: `${h.hour}:00`,
    total: parseInt(h.total)
  })) || [];

  return (
    // Batch 7 (Group B): calm/enterprise identity via the central tokens.
    // Styling scope only — filters, stats, alerts and both charts keep their
    // existing data flow, props and handlers.
    <div className="crm-calm crm-calm-page">
      {alerts.count > 0 && (
        <div className="crm-alert">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle size={18} />
            <span>{alerts.count} مكالمة فائتة تجاوزت {alerts.thresholdMinutes} دقيقة بدون متابعة!</span>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={async () => {
              if (!window.confirm('سيتم تجاهل كل التحذيرات الحالية وإعادة الاحتساب من الآن. هل أنت متأكد؟')) return;
              const token = localStorage.getItem('token');
              await fetch('/api/calls/alerts/dismiss', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
              fetchData();
            }}
          >إعادة احتساب</button>
        </div>
      )}

      <div className="page-header">
        <h1 className="page-title">لوحة التحكم</h1>
      </div>

      <div className="crm-filterbar">
        {DATE_FILTERS.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${activeFilter === f.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => handleDateFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="lbl" style={{ marginRight: 8 }}>أو فترة مخصصة:</span>
        <span className="lbl">من</span><input type="date" value={customFrom} onChange={e => applyCustomRange(e.target.value, customTo)} className="filter-input" style={{ width: 150 }} />
        <span className="lbl">إلى</span>
        <input type="date" value={customTo} onChange={e => applyCustomRange(customFrom, e.target.value)} className="filter-input" style={{ width: 150 }} />
        <MultiSelectQueues queues={queues} selected={queue} onChange={setQueue} />
        {user?.role === 'admin' && (
          <select className="filter-select" value={agentExtension} onChange={e => setAgentExtension(e.target.value)}>
            <option value="">كل الموظفين</option>
            {agents.map(a => <option key={a.extension} value={a.extension}>{a.fullName} ({a.extension})</option>)}
          </select>
        )}
      </div>

      <div className="crm-statgrid">
        <div className="crm-statcard">
          <div className="ico accent"><FiPhone /></div>
          <div className="val">{stats?.totalCalls || 0}</div>
          <div className="lbl">إجمالي المكالمات</div>
        </div>
        <div className="crm-statcard">
          <div className="ico success"><FiPhoneIncoming /></div>
          <div className="val">{stats?.answeredCalls || 0}</div>
          <div className="lbl">مردود عليها</div>
        </div>
        <div className="crm-statcard">
          <div className="ico danger"><FiPhoneMissed /></div>
          <div className="val">{stats?.missedCalls || 0}</div>
          <div className="lbl">فائتة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico info"><FiActivity /></div>
          <div className="val">{stats?.responseRate || 0}%</div>
          <div className="lbl">نسبة الرد</div>
        </div>
        {/* The three follow-up states are now shown separately. "بانتظار المتابعة"
            uses pendingOnlyFollowUp (pending only); pendingFollowUp — which
            merges pending + in_progress + retry_later — is still returned by the
            API and is used as the fallback for older responses. */}
        <div className="crm-statcard">
          <div className="ico warn"><FiPhoneOff /></div>
          <div className="val">{stats?.pendingOnlyFollowUp ?? stats?.pendingFollowUp ?? 0}</div>
          <div className="lbl">بانتظار المتابعة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico info"><FiActivity /></div>
          <div className="val">{stats?.inProgressFollowUp || 0}</div>
          <div className="lbl">جاري المتابعة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico success"><FiCheckCircle /></div>
          <div className="val">{stats?.callbackDone || 0}</div>
          <div className="lbl">تمت المتابعة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico neutral"><FiClock /></div>
          <div className="val">{stats?.retryLater || 0}</div>
          <div className="lbl">إعادة محاولة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico info"><FiActivity /></div>
          <div className="val">{stats?.slaRate || 0}%</div>
          <div className="lbl">نسبة SLA</div>
        </div>
      </div>


      <h3 className="crm-section-title">إجمالي العملاء (أرقام فريدة)</h3>
      <div className="crm-statgrid">
        <div className="crm-statcard">
          <div className="ico accent"><FiUsers /></div>
          <div className="val">{stats?.totalClients || 0}</div>
          <div className="lbl">إجمالي العملاء</div>
        </div>
        <div className="crm-statcard">
          <div className="ico success"><FiUsers /></div>
          <div className="val">{stats?.answeredClients || 0}</div>
          <div className="lbl">تم الرد عليهم</div>
        </div>
        <div className="crm-statcard">
          <div className="ico danger"><FiUsers /></div>
          <div className="val">{stats?.missedClients || 0}</div>
          <div className="lbl">لم يتم الرد</div>
        </div>
        <div className="crm-statcard">
          <div className="ico warn"><FiUsers /></div>
          <div className="val">{stats?.clientsMissedBeforeQueue || 0}</div>
          <div className="lbl">أغلقوا أثناء الترحيب</div>
        </div>
        <div className="crm-statcard">
          <div className="ico danger"><FiUsers /></div>
          <div className="val">{stats?.clientsAbandoned || 0}</div>
          <div className="lbl">لم يُرد وأغلقوا</div>
        </div>
        <div className="crm-statcard">
          <div className="ico success"><FiCheckCircle /></div>
          <div className="val">{stats?.clientsFollowedUp || 0}</div>
          <div className="lbl">تمت متابعتهم</div>
        </div>
        {/* Same split as the call-level row above. clientsPendingFollowUp merges
            pending + in_progress + retry_later, so showing it next to a
            "جاري المتابعة" card would count the in-progress numbers twice.
            "بانتظار المتابعة" therefore reads clientsPendingOnlyFollowUp, with
            the merged figure kept as the fallback for older API responses. */}
        <div className="crm-statcard">
          <div className="ico warn"><FiClock /></div>
          <div className="val">{stats?.clientsPendingOnlyFollowUp ?? stats?.clientsPendingFollowUp ?? 0}</div>
          <div className="lbl">بانتظار المتابعة</div>
        </div>
        <div className="crm-statcard">
          <div className="ico info"><FiActivity /></div>
          <div className="val">{stats?.clientsInProgressFollowUp || 0}</div>
          <div className="lbl">جاري المتابعة</div>
        </div>
      </div>

      {/* Charts: the CARDS below are restyled with tokens. Every recharts
          component, its data prop and its series config are byte-identical to
          before — the only chart-internal edits are colour strings (bar fill,
          grid stroke, axis tick ink), which carry no data or rendering logic. */}
      <div className="crm-chartgrid">
        <div className="crm-chartcard">
          <h3>المكالمات حسب الساعة</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={hourData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_GRID} />
              <XAxis dataKey="hour" tick={{ fontSize: 11, fill: CHART_AXIS_INK }} interval={1} />
              <YAxis tick={{ fontSize: 12, fill: CHART_AXIS_INK }} />
              <Tooltip formatter={(v) => [v, 'مكالمات']} />
              <Bar dataKey="total" fill={CHART_ACCENT} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="crm-chartcard">
          <h3>المكالمات حسب الكيو</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ResponsiveContainer width="50%" height={280}>
              <PieChart>
                <Pie data={queueData} cx="50%" cy="50%" outerRadius={100} dataKey="value" stroke="none">
                  {queueData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [v, 'مكالمات']} />
              </PieChart>
            </ResponsiveContainer>
            {/* direct-labelled legend: every slice is named and valued, so
                identity never rests on colour alone */}
            <div className="crm-chartlegend">
              {queueData.map((q, i) => (
                <div key={i} className="row">
                  <div className="sw" style={{ background: q.fill }}></div>
                  <span className="nm">{q.name}</span>
                  <b>{q.value}</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;